// Phase 19 · Community V2 · Group detail (sidebar channels + chat pane)
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Users, Hash, Send, Plus, LogIn, UserMinus, Settings, Paperclip } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import NotificationBell from "../../components/community_v2/NotificationBell";
import MarketplaceEmbedCard from "../../components/community/MarketplaceEmbedCard";
import MiniLandingEmbedCard from "../../components/community/MiniLandingEmbedCard";
import AttachmentPicker from "../../components/community_v2/AttachmentPicker";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtRel(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60) return "just now";
        if (diff < 3600) return Math.floor(diff / 60) + "m";
        if (diff < 86400) return Math.floor(diff / 3600) + "h";
        return Math.floor(diff / 86400) + "d";
    } catch (e) { return ""; }
}

function ChannelRow({ ch, active, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`w-full text-left px-3 py-1.5 rounded transition-all flex items-center gap-2 text-sm ${active ? "bg-[#FFFFFF]/10 text-[#FFFFFF]" : "text-neutral-300 hover:bg-white/5"}`}
            data-testid={`channel-row-${ch.slug}`}
        >
            <Hash size={14} className="text-neutral-500" />
            <span className="flex-1 truncate">{ch.slug}</span>
            {ch.message_count > 0 && (
                <span className="text-[10px] text-neutral-500">{ch.message_count}</span>
            )}
        </button>
    );
}

function ChannelMessage({ m, currentUserId }) {
    const isMe = m.sender_id === currentUserId;
    return (
        <div className="flex gap-2 px-3 py-2 hover:bg-white/5 rounded" data-testid={`ch-msg-${m.id}`}>
            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-neutral-900 font-bold text-xs flex-shrink-0">
                {m.sender_username?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs text-neutral-500">
                    <Link to={`/profile/${m.sender_username}`} className={`font-semibold ${isMe ? "text-[#CCCCCC]" : "text-neutral-200"} hover:underline`}>
                        @{m.sender_username}
                    </Link>
                    <span>{fmtRel(m.created_at)}</span>
                </div>
                {m.body && <p className="text-sm text-neutral-100 whitespace-pre-wrap break-words mt-0.5">{m.body}</p>}
                {m.message_type === "asset_marketplace" && m.marketplace_preview && (
                    <div className="mt-2 max-w-md"><MarketplaceEmbedCard preview={m.marketplace_preview} /></div>
                )}
                {m.message_type === "mini_landing" && m.mini_landing_preview && (
                    <div className="mt-2 max-w-md"><MiniLandingEmbedCard preview={m.mini_landing_preview} /></div>
                )}
            </div>
        </div>
    );
}

function CreateChannelModal({ groupId, onClose, onCreated }) {
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!name.trim() || name.trim().length < 3) return;
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/community/groups/${groupId}/channels`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ name: name.trim(), channel_type: "custom" }),
            });
            if (!r.ok) {
                const err = await r.json().catch(() => ({}));
                toast.error(err.detail?.error || "Channel create failed");
                return;
            }
            const d = await r.json();
            toast.success("Channel created");
            onCreated?.(d);
        } catch { toast.error("Network error"); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
            <div className="bg-[#111111] border border-[#1A1A1A] rounded-2xl w-full max-w-md p-6">
                <h2 className="text-lg font-semibold text-neutral-100 mb-4">New channel</h2>
                <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="channel-name"
                    maxLength={30}
                    className="w-full bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF] mb-4"
                    data-testid="channel-name-input"
                />
                <div className="flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-neutral-400">Cancel</button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy}
                        className="px-4 py-2 text-sm bg-white text-neutral-900 font-semibold rounded"
                        data-testid="channel-create-submit"
                    >
                        {busy ? "..." : "Create"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function JoinRequestsPanel({ groupId, onChange }) {
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await fetch(`${API}/api/community/groups/${groupId}/join-requests?status=pending`, {
                credentials: "include",
            });
            if (r.ok) {
                const d = await r.json();
                setRows(d.items || []);
            }
        } catch { /* noop */ }
    }, [groupId]);

    useEffect(() => { load(); }, [load]);

    const decide = async (reqId, action) => {
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/community/groups/${groupId}/join-requests/${reqId}/${action}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ reason: "" }),
            });
            if (r.ok) {
                toast.success(`Request ${action}d`);
                load(); onChange?.();
            } else {
                toast.error("Action failed");
            }
        } catch { toast.error("Network error"); }
        setBusy(false);
    };

    if (!rows.length) return null;
    return (
        <div className="mt-4 pt-4 border-t border-[#1A1A1A]" data-testid="join-requests-panel">
            <p className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">
                Join requests <span className="text-[#FFFFFF]">({rows.length})</span>
            </p>
            <div className="space-y-1.5">
                {rows.map((r) => (
                    <div key={r.id} className="flex items-center justify-between text-xs bg-[#0A0A0A] border border-[#1A1A1A] rounded px-2 py-1.5" data-testid={`join-req-row-${r.id}`}>
                        <div className="flex-1 min-w-0">
                            <p className="text-neutral-200 truncate">@{r.username}</p>
                            {r.message && <p className="text-[10px] text-neutral-500 truncate">{r.message}</p>}
                        </div>
                        <div className="flex gap-1">
                            <button
                                type="button"
                                onClick={() => decide(r.id, "approve")}
                                disabled={busy}
                                className="px-2 py-0.5 text-[10px] bg-[#CCCCCC]/20 text-[#CCCCCC] rounded hover:bg-[#CCCCCC]/30"
                                data-testid={`join-req-approve-${r.id}`}
                            >
                                Approve
                            </button>
                            <button
                                type="button"
                                onClick={() => decide(r.id, "reject")}
                                disabled={busy}
                                className="px-2 py-0.5 text-[10px] bg-[#FAFAFA]/20 text-[#FAFAFA] rounded hover:bg-[#FAFAFA]/30"
                                data-testid={`join-req-reject-${r.id}`}
                            >
                                Reject
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function GroupDetail() {
    const { groupId, channelId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [group, setGroup] = useState(null);
    const [currentChannel, setCurrentChannel] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [creating, setCreating] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [showAttachment, setShowAttachment] = useState(false);
    const messagesEndRef = useRef(null);

    const loadGroup = useCallback(async () => {
        try {
            const r = await fetch(`${API}/api/community/groups/${groupId}`, { credentials: "include" });
            if (r.status === 503) { setDisabled(true); return; }
            if (r.ok) {
                const d = await r.json();
                setGroup(d);
                const channels = d.channels || [];
                if (channelId) {
                    const found = channels.find((c) => c.id === channelId || c.slug === channelId);
                    if (found) setCurrentChannel(found);
                    else if (channels.length) setCurrentChannel(channels[0]);
                } else if (!currentChannel && channels.length) {
                    setCurrentChannel(channels[0]);
                }
            }
        } catch {}
    }, [groupId, channelId, currentChannel]);

    const loadMessages = useCallback(async (chId) => {
        if (!chId) return;
        try {
            const r = await fetch(`${API}/api/community/channels/${chId}/messages?limit=200`, { credentials: "include" });
            if (r.status === 403) { setMessages([]); return; }
            if (r.ok) {
                const d = await r.json();
                setMessages(d.items || []);
                setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 50);
            }
        } catch {}
    }, []);

    useEffect(() => { loadGroup(); }, [loadGroup]);
    useEffect(() => { if (currentChannel) loadMessages(currentChannel.id); }, [currentChannel, loadMessages]);

    // Polling channel messages 30s (Visibility API pause)
    useEffect(() => {
        if (!currentChannel) return undefined;
        const poll = () => {
            if (typeof document !== "undefined" && document.hidden) return;
            loadMessages(currentChannel.id);
        };
        const id = setInterval(poll, 30000);
        const onVis = () => { if (!document.hidden) poll(); };
        document.addEventListener("visibilitychange", onVis);
        return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
    }, [currentChannel, loadMessages]);

    const membership = group?.viewer_membership;
    const isModerator = membership && (membership.role === "owner" || membership.role === "moderator");

    const join = async () => {
        try {
            const r = await fetch(`${API}/api/community/groups/${groupId}/join`, {
                method: "POST", credentials: "include",
            });
            if (r.ok) { toast.success("Joined"); loadGroup(); }
            else toast.error("Cannot join");
        } catch {}
    };
    const leave = async () => {
        try {
            const r = await fetch(`${API}/api/community/groups/${groupId}/leave`, {
                method: "POST", credentials: "include",
            });
            if (r.ok) { toast.success("Left"); loadGroup(); }
            else toast.error("Cannot leave");
        } catch {}
    };

    const send = async (payload = null) => {
        const bodyPayload = payload || { message_type: "text", body: text.trim() };
        if (!currentChannel) return;
        if (bodyPayload.message_type === "text" && !bodyPayload.body) return;
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/community/channels/${currentChannel.id}/messages`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify(bodyPayload),
            });
            if (!r.ok) {
                toast.error(r.status === 429 ? "Slow down" : "Send failed");
                return;
            }
            setText("");
            loadMessages(currentChannel.id);
        } catch { toast.error("Network error"); }
        finally { setBusy(false); }
    };

    const sendAttachment = (item) => {
        if (!item) return;
        const payload = { message_type: item.kind };
        if (item.kind === "asset_marketplace") payload.marketplace_asset_ref = item.ref;
        else if (item.kind === "mini_landing") payload.mini_landing_ref = item.ref;
        send(payload);
    };

    const selectChannel = (ch) => {
        setCurrentChannel(ch);
        navigate(`/groups/${groupId}/channel/${ch.slug || ch.id}`, { replace: true });
    };

    if (!group && !disabled) return (
        <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
            <p className="text-neutral-500 text-sm">Loading…</p>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] font-inter flex flex-col" data-testid="group-detail-page">
            <header className="border-b border-[#1A1A1A] backdrop-blur-md bg-[#0A0A0A]/80 sticky top-0 z-40">
                <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link to="/groups" className="text-neutral-400 hover:text-neutral-200 flex items-center gap-1 text-sm">
                            <ArrowLeft size={16} /> Groups
                        </Link>
                        {group && (
                            <>
                                <span className="text-neutral-600">·</span>
                                <h1 className="text-base font-semibold" data-testid="group-name-header">{group.name}</h1>
                            </>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {group && user && !membership && (
                            <button
                                type="button"
                                onClick={join}
                                className="inline-flex items-center gap-1 bg-white text-neutral-900 font-semibold px-3 py-1.5 rounded-lg text-xs"
                                data-testid="group-join-btn"
                            >
                                <LogIn size={13} /> Join
                            </button>
                        )}
                        {group && membership && membership.role !== "owner" && (
                            <button
                                type="button"
                                onClick={leave}
                                className="inline-flex items-center gap-1 border border-[#1A1A1A] text-neutral-400 hover:text-neutral-100 px-3 py-1.5 rounded-lg text-xs"
                                data-testid="group-leave-btn"
                            >
                                <UserMinus size={13} /> Leave
                            </button>
                        )}
                        {user && <NotificationBell enabled />}
                    </div>
                </div>
            </header>

            <main className="flex-1 max-w-6xl mx-auto w-full px-4 py-4">
                {disabled && (
                    <div className="bg-[#FAFAFA]/10 border border-[#FAFAFA]/30 rounded-lg p-4 text-sm text-[#FAFAFA]">
                        Groups disabled by admin.
                    </div>
                )}
                {group && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 h-[calc(100vh-8rem)]">
                        {/* Channels sidebar */}
                        <aside className="md:col-span-1 bg-[#111111] border border-[#1A1A1A] rounded-xl p-3 overflow-y-auto">
                            <div className="flex items-center justify-between mb-3">
                                <p className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">
                                    Channels
                                </p>
                                {isModerator && (
                                    <button
                                        type="button"
                                        onClick={() => setCreating(true)}
                                        className="text-neutral-500 hover:text-[#FFFFFF]"
                                        title="New channel"
                                        data-testid="new-channel-btn"
                                    >
                                        <Plus size={14} />
                                    </button>
                                )}
                            </div>
                            <div className="space-y-1">
                                {(group.channels || []).map(ch => (
                                    <ChannelRow
                                        key={ch.id}
                                        ch={ch}
                                        active={currentChannel?.id === ch.id}
                                        onClick={() => selectChannel(ch)}
                                    />
                                ))}
                            </div>
                            <div className="mt-4 pt-4 border-t border-[#1A1A1A] text-xs text-neutral-500">
                                <p className="flex items-center gap-1.5">
                                    <Users size={12} /> {group.member_count || 1} members
                                </p>
                                <p className="mt-1 text-[10px]">
                                    by @{group.owner_username}
                                </p>
                                {group.visibility && group.visibility !== "public" && (
                                    <p className="mt-1 text-[10px] uppercase tracking-wider text-[#CCCCCC]">
                                        {group.visibility}
                                    </p>
                                )}
                            </div>
                            {membership?.role === "owner" && group.visibility !== "public" && (
                                <JoinRequestsPanel groupId={groupId} onChange={loadGroup} />
                            )}
                        </aside>

                        {/* Chat pane */}
                        <section className="md:col-span-3 bg-[#111111] border border-[#1A1A1A] rounded-xl flex flex-col overflow-hidden">
                            {currentChannel ? (
                                <>
                                    <div className="p-3 border-b border-[#1A1A1A] flex items-center gap-2">
                                        <Hash size={16} className="text-neutral-500" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-neutral-100">
                                                {currentChannel.slug}
                                            </p>
                                            {currentChannel.description && (
                                                <p className="text-[10px] text-neutral-500 truncate">
                                                    {currentChannel.description}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex-1 overflow-y-auto py-2" data-testid="channel-messages-list">
                                        {messages.length === 0 && (
                                            <p className="text-xs text-neutral-500 italic text-center py-8">
                                                {membership ? "No messages yet. Be the first!" : "Join to see messages"}
                                            </p>
                                        )}
                                        {messages.map(m => <ChannelMessage key={m.id} m={m} currentUserId={user?.id} />)}
                                        <div ref={messagesEndRef} />
                                    </div>
                                    {membership && (
                                        <div className="p-3 border-t border-[#1A1A1A] flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setShowAttachment(true)}
                                                className="w-9 h-9 rounded-lg border border-[#1A1A1A] text-neutral-400 hover:text-[#FFFFFF] hover:border-[#FFFFFF]/40 flex items-center justify-center flex-shrink-0"
                                                title="Share marketplace asset or mini-landing"
                                                data-testid="channel-attachment-btn"
                                            >
                                                <Paperclip size={16} />
                                            </button>
                                            <input
                                                type="text"
                                                value={text}
                                                onChange={(e) => setText(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(); }}
                                                placeholder={`Message #${currentChannel.slug}…`}
                                                className="flex-1 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                                                data-testid="channel-input"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => send()}
                                                disabled={busy || !text.trim()}
                                                className="px-3 py-2 bg-white text-neutral-900 font-semibold rounded-lg disabled:opacity-40"
                                                data-testid="channel-send-btn"
                                            >
                                                <Send size={16} />
                                            </button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="flex-1 flex items-center justify-center text-neutral-500 text-sm">
                                    Select a channel
                                </div>
                            )}
                        </section>
                    </div>
                )}
            </main>

            {creating && (
                <CreateChannelModal
                    groupId={groupId}
                    onClose={() => setCreating(false)}
                    onCreated={() => { setCreating(false); loadGroup(); }}
                />
            )}
            {showAttachment && (
                <AttachmentPicker
                    onClose={() => setShowAttachment(false)}
                    onPick={sendAttachment}
                />
            )}
        </div>
    );
}
